// Element value utilities extracted from src/modules/Services/ServicesUtils.js

import _ from "lodash";
import moment from "moment";
import i18next from "i18next";
import {
  generateNestedSelectedData,
  convertDurationToString,
  buildSlimTree,
  getUniqAssociatedParentsOfNodes,
  mergeAssociatedParentsWithNodes,
  getStyleStrippedText,
  getPYPElementSetFromNodes,
  htmlToText,
  getRethumbUrl,
} from "Utils";
import {
  getSubjectBasicDetailsFromCache,
  getPypElementFromCache,
  getFocusAreaFromCache,
} from "modules/CommonGraphqlHelpers";
import { getNodeParents } from "Utils/nodeParentHelpers";
import ACLStore from "lib/aclStore";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
} from "Constants/stringConstants";
import {
  TAGGABLE_PYP_ELEMENTS_V2,
  TAGGABLE_DP_ELEMENTS_V2,
  TAGGABLE_MYP_ELEMENTS_V2,
  TAGGABLE_UBD_ELEMENTS,
} from "Constants/taggableElements";
import {
  getPlannerElementNodeFromCache,
  getSubjectDetailsFromCache,
} from "Projects/modules/ProjectGraphqlHelpers";
import {
  ORG_RESOURCE_TYPE_WORKSHEET,
  ASSESSMENT_TYPE_WORKSHEET,
} from "Worksheet/modules/WorksheetConstants";
import {
  ASSESSMENT_TOOLS,
  AI_TRANSLATION_LANGUAGES,
  STUDENT_COMMUNICATION_MODES,
  TUTOR_COMMUNICATION_MODES,
} from "@toddle-edu/common-utils";
import { checkForIframeEmbedInHtmlString } from "Utils/HTMLHelpers";
import { SIZE, EVALUATION_METHOD } from "modules/Services/ServicesConstants";
import { isSinglePageAndNewUnitView } from "UnitPlans/modules/UnitViewModeUtils";
import {
  generateFlatHierarchyWithValues,
  mergeArraysById,
} from "Utils/BenchmarkDataHelper";
import {
  pypElementMapping,
  pypElementSetMapping,
} from "Constants/plannerElementMappings";
import { shouldShowFlatListForType } from "AppComponents/DatasetTaggingBody/modules/Utils";

// ---------------------------------------------------------------------------
// Helpers inlined from domain-specific modules (deep route paths and feature
// folders) to keep this top-level module from reaching into them. If any
// source diverges, sync manually.
// ---------------------------------------------------------------------------

// Inlined from src/routes/routes/Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils.js
const getAssessmentCoverRethumb = url => {
  if (url) {
    return getRethumbUrl({
      width: 1024,
      height: 1024,
      imageUrl: url,
      fitIn: true,
    });
  }
  return null;
};

// Inlined from src/routes/routes/Teacher/routes/Courses/routes/Course/routes/UnitPlans/components/Assessments/modules/Utils.js
const getFieldValue = ({ fields, uid }) => {
  const fieldData = _.find(fields, { uid });
  return _.get(fieldData, "value", []) || [];
};

// Inlined from src/Fields/FieldUtils.js
const getElementValueForPortedFilteredByField = ({
  resolvedMinimalTree,
  value,
}) => {
  return generateNestedSelectedData({
    ...getPYPElementSetFromNodes({
      nodes: _.get(resolvedMinimalTree, "nodes", []),
    }),
    value,
  });
};

// Inlined from src/routes/routes/Administrator/routes/MYPAcademicSetup/routes/ScoreCategories/modules/ScoreCategoriesModule.js
const getScoreDetails = ({ assessmentTools }) => {
  const _assessmentTools = !_.isEmpty(assessmentTools?.edges)
    ? assessmentTools?.edges
    : assessmentTools;

  const scoreTool = _.find(_assessmentTools, ({ node } = {}) => {
    return _.get(node, "assessmentToolType") === "SCORE";
  });

  const scoreToolData = _.get(
    scoreTool,
    "node.assessmentToolItem.scoreToolData",
    {}
  );

  const maxScore = _.get(scoreToolData, "maxScore", null);
  const scoreToolId = _.get(scoreToolData, "id");

  return {
    isScoreEnabled: !_.isEmpty(scoreTool),
    maxScore: maxScore ?? "",
    scoreToolId,
  };
};

const getScoreDetailsMemoize = _.memoize(
  params => getScoreDetails(params),
  params => JSON.stringify(params)
);

export const getPypElementLabel = ({ type, value }) => {
  if (value && !Array.isArray(value)) {
    const pypElement = getPypElementFromCache({ type, id: value });

    return _.get(pypElement, "label", "N/A");
  } else {
    return _.filter(
      _.map(value, item => {
        const pypElement = getPypElementFromCache({ type, id: item });

        return _.get(pypElement, "label");
      }),
      val => !!val
    );
  }
};

export const getPlannerElementLabel = ({ nodes, value }) => {
  if (value && !Array.isArray(value)) {
    return _.get(_.find(nodes, { id: value }), "label", "");
  } else {
    return _.filter(
      _.map(value, item => {
        return _.get(_.find(nodes, { id: item }), "label", "");
      }),
      val => !!val
    );
  }
};

const getFocusAreaLabel = value => {
  if (value && !Array.isArray(value)) {
    return _.get(getFocusAreaFromCache(value), "label", "");
  } else {
    return _.map(value, item => {
      return _.get(getFocusAreaFromCache(item), "label", "");
    });
  }
};

const getMultiFieldValue = ({
  fieldObj,
  valueKeys,
  unitPlanFieldValue,
  isUnitPlan,
}) => {
  const subFields = _.get(fieldObj, "subFields", []);
  const valueObj = _.reduce(
    subFields,
    (result, field) => {
      const { key } = field;
      let subFieldValue = (valueKeys || {})[key];
      const subUnitPlanFieldValue = (unitPlanFieldValue || {})[key];
      subFieldValue = getElementValue({
        valueKeys: subFieldValue,
        fieldObj: field,
        unitPlanFieldValue: subUnitPlanFieldValue,
        isUnitPlan,
      });
      result[key] = subFieldValue;
      return result;
    },
    {}
  );

  return valueObj;
};

const checkIfWorldStudiesSubjectSelected = ({ unitPlanFields }) => {
  const subjectField = _.find(unitPlanFields, field => field.uid === "subject");
  const subjectId = _.get(subjectField, "value[0]");
  const subjectValue = getSubjectDetailsFromCache({ subjectId });
  const isWorldStudies = _.get(
    subjectValue,
    "metadata.is_world_studies",
    false
  );
  return isWorldStudies;
};

export const getElementValue = ({
  fieldUID,
  valueKeys,
  unitPlanFieldValue,
  benchmarkSet,
  fieldObj,
  isUnitPlan = false,
  isPost = false,
  resolvedValue,
  isLocalisedTemplate = true,
  subjectIds,
  shouldStripRichTextStyles = false,
  t = () => {},
  deriveSubjectIdsFromNodes = false,
  isCommunity = false,
  unitPlanFields,
  subjects,
  plannerElements,
  currentAssessmentTypes,
  skipRemoveSubjectCheck = false,
  shouldReturnDurationInWeeks = false,
  skipMergeStandardsAssociatedParents = false,
  shouldGenerateParentChildRelation = false,
  shouldGenerateFlatHierarchyWithValues = false,
  includeNodeSet = true,
}) => {
  switch (fieldUID) {
    case "disciplinaryContent": {
      const contentFields = _.filter(unitPlanFields, field =>
        _.includes(field.uid, "disciplinaryContent")
      );
      const filteredFields = _.filter(contentFields, field =>
        _.get(_.find(unitPlanFields, { uid: field.uid }), "value", null)
      );

      let result = "";

      _.forEach(filteredFields, filteredField => {
        const subjectId = _.last(_.split(filteredField.uid, "_"));
        const subjectName = _.get(
          _.find(subjects, { id: subjectId }),
          "name",
          " "
        );

        result =
          result +
          `<div><b>${subjectName}</b><br/>${_.get(
            _.find(unitPlanFields, { uid: filteredField.uid }),
            "value",
            null
          )}</div><br/>`;
      });

      return result;
    }
    case "hours":
      return _.isNumber(valueKeys) ? _.toString(valueKeys) : "-";
    case "dateDuration":
      if (valueKeys && valueKeys.startDate && valueKeys.endDate) {
        if (shouldReturnDurationInWeeks) {
          const startDate = moment(valueKeys.startDate);
          const endDate = moment(valueKeys.endDate);
          const durationInWeeks = endDate.diff(startDate, "weeks");

          return t("moment:weeks", {
            count: durationInWeeks,
          });
        }

        return t("common:start_date_end_date_label", {
          startDate: moment(valueKeys.startDate).format("DD MMM YYYY"),
          endDate: moment(valueKeys.endDate).format("DD MMM YYYY"),
        });
      }
      return null;
    case "centralIdea":
      return isUnitPlan ? valueKeys : unitPlanFieldValue;
    case "focusAreas":
      return _.join(getFocusAreaLabel(valueKeys), "; ");
    case "subjectGroup":
      return _.map(
        _.get(resolvedValue, "subjectGroups", []),
        subjectGroup => subjectGroup.name
      );
    case "subjects":
      return _.map(
        _.get(resolvedValue, "subjects", []),
        subjectGroup => subjectGroup.name
      );
    case "subject": {
      if (!_.isEmpty(valueKeys)) {
        const subject = getSubjectBasicDetailsFromCache(valueKeys[0]);
        return subject?.name;
      }
      return null;
    }
    case "subjectV2": {
      if (!_.isEmpty(valueKeys)) {
        const subjectIds = _.first(valueKeys) || [];
        const subjects = _.map(subjectIds, subjectId =>
          getSubjectBasicDetailsFromCache(subjectId)
        );
        return _.chain(subjects).map("name").compact().join(", ").value();
      }
      return null;
    }
    case "wsSubject1":
    case "wsSubject2": {
      const isWorldStudies = checkIfWorldStudiesSubjectSelected({
        unitPlanFields,
      });

      if (isWorldStudies && !_.isEmpty(valueKeys)) {
        const subject = getSubjectBasicDetailsFromCache(valueKeys[0]);
        return subject?.name;
      }
      return null;
    }
    case "subjectLevel":
      return _.map(_.get(resolvedValue, "genericTags", []), tag => tag.label);
    case "category": {
      if (!_.isEmpty(valueKeys)) {
        const category = getPlannerElementNodeFromCache({
          categoryId: valueKeys[0],
        });
        if (category.subText) {
          return `${category?.label} ${category?.subText}`;
        }
        return category?.label;
      }
      return null;
    }
    case "wsTheme": {
      const isWorldStudies = checkIfWorldStudiesSubjectSelected({
        unitPlanFields,
      });

      if (isWorldStudies && !_.isEmpty(valueKeys)) {
        const category = getPlannerElementNodeFromCache({
          categoryId: valueKeys[0],
        });
        return category?.label;
      }
      return null;
    }
    case "responseLanguage": {
      if (!_.isEmpty(valueKeys)) {
        const subjectField = _.find(
          unitPlanFields,
          field => field.uid === "subject"
        );
        const subjectId = _.get(subjectField, "value[0]");
        const subjectObj = getSubjectDetailsFromCache({ subjectId });
        const language = _.find(
          _.get(subjectObj, "flatVariants", []),
          item => item?.responseLanguage?.id === valueKeys[0]
        );
        return language?.responseLanguage?.label;
      }
      return null;
    }
    case "responseLanguageV2": {
      if (!_.isEmpty(valueKeys)) {
        const responseLanguageId = _.first(valueKeys);

        const subjectField = _.find(
          unitPlanFields,
          field => field.uid === "subjectV2"
        );
        const subjectIds = _.get(subjectField, "value[0]", []);

        const subjectObjs = _.map(subjectIds, subjectId =>
          getSubjectDetailsFromCache({ subjectId })
        );

        const languages = _.compact(
          _.map(subjectObjs, subjectObj => {
            const language = _.find(
              _.get(subjectObj, "flatVariants", []),
              item => item?.responseLanguage?.id === responseLanguageId
            );
            return language?.responseLanguage?.label;
          })
        );
        return _.first(languages) || null;
      }
      return null;
    }
    case "rpResponseLanguage": {
      if (!_.isEmpty(valueKeys)) {
        const language = _.find(
          _.get(fieldObj, "config.options", []),
          language => language.value === valueKeys[0]
        );
        return language.label;
      }
      return null;
    }
    case "action":
      return isPost && !ACLStore.can("FeatureFlag:EnableAction")
        ? "Action"
        : getPypElementLabel({
            value: valueKeys,
            type: pypElementMapping[fieldUID],
          });
    case "theme":
    case "lp":
    case "concepts":
    case "learnerProfileDP":
      return getPypElementLabel({
        value: valueKeys,
        type: pypElementMapping[fieldUID],
      });
    case "lois":
    case "goalList":
      return getNotPypInputListLabel({
        values: valueKeys,
        unitPlanFieldValue,
        isUnitPlan,
      });
    case "atls":
      return getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "atls", []),
        shouldGenerateFlatHierarchyWithValues,
        includeNodeSet: false,
      });
    case "syllabusContentDP": {
      const syllabusContent = getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "nodes", []),
        shouldGenerateParentChildRelation: false,
      });

      return syllabusContent;
    }
    case "atlDP": {
      const atlDP = getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "nodes", []),
        shouldGenerateParentChildRelation: false,
      });

      return atlDP;
    }
    case "subjectStandardDP": {
      if (shouldGenerateParentChildRelation) {
        const sortedStandards = _.orderBy(
          _.get(resolvedValue, "nodes", []),
          [item => _.get(item, "standardsWrapper.standardSet.id")],
          ["asc"]
        );
        return getBenchMarkValueMemoized({
          shouldGenerateFlatHierarchyWithValues,
          values: valueKeys,
          nodes: sortedStandards,
          shouldGenerateParentChildRelation: shouldGenerateParentChildRelation,
        });
      }
      const subjectStandardDP = getBenchMarkValueMemoized({
        shouldGenerateFlatHierarchyWithValues,
        values: valueKeys,
        nodes: _.get(resolvedValue, "nodes", []),
        shouldGenerateParentChildRelation: false,
      });

      return subjectStandardDP;
    }
    case "benchmarks":
      // HACK: In the community no unitplan subject reference is there
      if (!_.isEmpty(subjectIds) && !isCommunity) {
        const benchmarks = _.get(resolvedValue, "benchmarks", []);
        resolvedValue = {
          ...resolvedValue,
          benchmarks: _.filter(benchmarks, item =>
            _.includes(subjectIds, item.subject)
          ),
        };
      }

      if (skipRemoveSubjectCheck) {
        const sortedBenchmarks = _.orderBy(
          _.get(resolvedValue, "benchmarks", []),
          [item => _.get(item, "standardsWrapper.standardSet.id")],
          ["asc"]
        );
        return getBenchMarkValueMemoized({
          shouldGenerateFlatHierarchyWithValues,
          values: valueKeys,
          benchmarkSet,
          nodes: sortedBenchmarks,
          startDepth: isPost ? _.first(sortedBenchmarks)?.depth : undefined,
        });
      }

      return getBenchMarkValueMemoized({
        shouldGenerateFlatHierarchyWithValues,
        values: valueKeys,
        benchmarkSet,
        nodes: _.get(resolvedValue, "benchmarks", []),
      });
    case "voiceInstruction":
      return _.get(resolvedValue, "attachment", {});
    case "studentTemplate":
    case "openingMessageResource":
      resolvedValue = {
        ...resolvedValue,
        attachmentGroups: _.map(
          _.get(resolvedValue, "attachmentGroups", []),
          group => {
            return { ...group, isPost: true };
          }
        ),
      };
      return _.get(resolvedValue, "attachmentGroups", {});
    case "aiExamplars":
    case "ltiTemplate":
      resolvedValue = {
        ...resolvedValue,
        attachmentGroups: _.map(
          _.get(resolvedValue, "attachmentGroups", []),
          group => {
            return { ...group };
          }
        ),
      };
      return _.get(resolvedValue, "attachmentGroups", {});
    case "duration":
      return isUnitPlan
        ? valueKeys
          ? `${valueKeys}h`
          : "0h"
        : convertDurationToString(valueKeys);
    case "assessmentType": {
      const localeLabel = _.get(
        currentAssessmentTypes?.[valueKeys],
        "localeLabel",
        ""
      );
      const label = _.get(currentAssessmentTypes?.[valueKeys], "label", "");
      return localeLabel ? t(localeLabel) : label;
    }
    case "documentation":
    case "measureAssessing":
    case "evaluationType":
    case "studentUnderstandCheckList":
    case "stageOneNTwoConnection":
    case "size":
      return getValueFromLabelValuePair({
        values: valueKeys,
        fieldObj,
        fieldUID,
        t,
        isLocalisedTemplate,
      });
    case "unitPlan": {
      const unitPlanTitle = _.get(resolvedValue, "unitPlan.title", {});
      return _.get(unitPlanTitle, "value", "");
    }
    case "pedagogicalApproachDP": {
      const plannerElementSet =
        _.find(plannerElements, {
          type: "DP_PEDAGOGICAL_APPROACH",
        }) || {};

      if (_.every(valueKeys, key => _.has(key, "value"))) {
        return _.map(
          _.filter(valueKeys, key => _.get(key, "isSelected", false)),
          "value"
        );
      }

      if (plannerElementSet) {
        const plannerElementNodes = _.get(plannerElementSet, "nodes", []);

        return _.filter(plannerElementNodes, node => {
          return _.includes(valueKeys, node?.id);
        }).map(node => node?.label);
      } else {
        return valueKeys;
      }
    }
    case "slStages":
    case "casStages": {
      const toggle = _.find(
        unitPlanFields,
        field => field.uid === fieldObj?.parentFieldUID
      );
      if (toggle?.value) {
        const value = getMultiFieldValue({
          fieldObj,
          isUnitPlan,
          unitPlanFieldValue,
          valueKeys,
        });
        const filteredObj = _.pickBy(value || {}, val => val);
        return !_.isEmpty(filteredObj) ? filteredObj : null;
      }
      return null;
    }
    case "image": {
      if (!isUnitPlan) return getAssessmentCoverRethumb(valueKeys);
      return valueKeys;
    }

    case "tutorSecondaryLanguage":
    case "tutorLanguage": {
      return _.get(
        _.find(AI_TRANSLATION_LANGUAGES, ({ id }) => id == valueKeys),
        "label"
      );
    }

    case "studentCommunicationMode": {
      return _.get(
        _.find(STUDENT_COMMUNICATION_MODES, ({ id }) => id === valueKeys),
        "label"
      );
    }
    case "tutorCommunicationMode": {
      return _.get(
        _.find(TUTOR_COMMUNICATION_MODES, ({ id }) => id === valueKeys),
        "label"
      );
    }
    case "ceLearningOutcomes":
    case "casLearningOutcomes":
    case "slLearningOutcomes": {
      const res = _.map(resolvedValue?.nodes, item => {
        const {
          metadata: { display_name: displayName, isCustom },
          label,
        } = item;
        if (isCustom) {
          return displayName || label;
        }
        return i18next.t("project:label1_with_label2_and_colon", {
          label1: displayName,
          label2: label,
        });
      });
      return res;
    }

    default:
      //Need Review @kunal
      // Following condition is generic to handle all planner elements except MYP_EXPLORATION
      if (
        _.get(fieldObj, "config.type") == "PLANNER_ELEMENT" &&
        !_.includes(
          [
            "MYP_EXPLORATION",
            "DP_PEDAGOGICAL_APPROACH",
            "DP_DIFFERENTIATION_APPROACH",
          ],
          _.get(fieldObj, "config.plannerElementConfig.type", "")
        )
      ) {
        return plannerElementValue({
          fieldObj,
          subjectIds,
          resolvedValue,
          value: valueKeys,
          deriveSubjectIdsFromNodes,
          skipRemoveSubjectCheck,
          skipMergeStandardsAssociatedParents,
          shouldGenerateFlatHierarchyWithValues,
          includeNodeSet,
        });
      } else {
        switch (_.get(fieldObj, "viewType", "")) {
          // Following condition is generic to handle MultiFieldComponent
          //E.g - inquiryQuestions in MYP
          case "MultiField": {
            return getMultiFieldValue({
              fieldObj,
              isUnitPlan,
              unitPlanFieldValue,
              valueKeys,
            });
          }

          // Following condition is generic to handle listview component
          case "ListView":
            //Added condition wheather field is dependent on unitplan or not
            //E.g - LOIS,GOALS e.t.c

            //Need Review @kunal
            if (_.get(fieldObj, "config.isUnitPlanDependent", false)) {
              return getNotPypInputListLabel({
                values: valueKeys,
                unitPlanFieldValue,
                isUnitPlan,
              });
            } else if (
              !_.isEmpty(_.get(fieldObj, "config.options", [])) &&
              _.get(fieldObj, "type", "") == "CheckList"
            ) {
              return getOptionLabels({
                options: fieldObj.config.options,
                values: valueKeys,
              });
            } else {
              return getLists({
                values: _.filter(valueKeys, val => {
                  /*Handle special case where there is a selection in Input List (E.g MYP_EXPLORATION)
                   * In this case, you have to show only selected items
                   */
                  const isSelected =
                    _.isUndefined(val.isSelected) || val.isSelected;
                  return (
                    !_.isEmpty(_.trim(_.get(val, "value", ""))) && isSelected
                  );
                }),
              });
            }

          case "TextView": {
            if (!_.isEmpty(_.get(fieldObj.config, "options", []))) {
              switch (_.get(fieldObj, "type", "")) {
                case "RadioButtonList":
                case "CheckList":
                case "MultiSelect": {
                  //Handle the case where field options are passed from template using radioButtonList
                  return getValueFromLabelValuePair({
                    values: valueKeys,
                    fieldObj,
                    fieldUID,
                    t,
                    isLocalisedTemplate,
                  });
                }
                default: {
                  return valueKeys;
                }
              }
            }
            return shouldStripRichTextStyles && valueKeys != null
              ? getStyleStrippedText({
                  text: valueKeys,
                  type: "RICH_TEXT_EDITOR",
                })
              : valueKeys;
          }

          case "InputNumberListWithTag": {
            return getValueForListWithTags({ valueKeys, fieldObj });
          }

          case "CheckListWithPopup": {
            const updatedValues = getValueForListWithTags({
              valueKeys,
              fieldObj,
            });

            const unitPlanInquiryQuestions = _.map(
              getFieldValuesOfMultiWindowTableMemoized({
                valueKeys: unitPlanFieldValue,
                fieldKey: "inquiryQuestions",
              }),
              ({ id }) => id
            );

            return _.filter(updatedValues, ({ id }) =>
              _.includes(unitPlanInquiryQuestions, id)
            );
          }

          case "ActionTable": {
            const rows = _.get(valueKeys, "rows");
            const columns = _.get(valueKeys, "columns");

            const filteredRows = _.filter(rows, row => {
              let isRowEmpty = true;
              const modifiedData = _.map(row.data, cell => {
                if (!_.isEmpty(cell.value)) {
                  const truncatedValue = _.trim(cell.value);
                  if (!_.isEmpty(truncatedValue)) {
                    isRowEmpty = false;
                  }
                  return { ...cell, value: truncatedValue };
                }
                return cell;
              });
              return !isRowEmpty ? { ...row, data: modifiedData } : null;
            });

            /**
             * This is needed as in UnitSummary, sometimes field is shown when its value is not empty,
             * regardless of the return value of checkEmptyElementValue.
             */
            if (_.isEmpty(filteredRows)) {
              return {};
            }

            return { rows: filteredRows, columns };
          }

          case "LearningIntentions": {
            return _.filter(valueKeys, val => {
              return (
                !_.isEmpty(_.trim(val?.value)) &&
                (val?.isSelected || _.isUndefined(val?.isSelected))
              );
            });
          }

          default:
            // Handle field values based on UI type.
            switch (_.get(fieldObj, "type", "")) {
              case "MultiSelect": {
                // Handle the case where field options are passed from template
                // in this UI type
                const options = _.get(fieldObj, "config.options", []);
                const selectedOptions = _.filter(options, option => {
                  return _.includes(valueKeys, option.value);
                });

                return _.map(selectedOptions, option => option.label);
              }

              case "NestedSelectComponent": {
                return plannerElementValue({
                  fieldObj,
                  subjectIds,
                  resolvedValue,
                  value: valueKeys,
                  deriveSubjectIdsFromNodes,
                });
              }

              case "MultiWindowTable": {
                return getViewModeValueForMultiWindowTable({
                  fieldObj,
                  valueKeys,
                });
              }

              default:
                return valueKeys;
            }
        }
      }
  }
};

const getOptionLabels = ({ options, values }) => {
  return _.map(values, value => {
    return _.get(
      _.find(options, option => option.value == value),
      "label",
      ""
    );
  });
};

export const plannerElementValue = ({
  value,
  fieldObj,
  subjectIds,
  resolvedValue,
  deriveSubjectIdsFromNodes = false,
  skipRemoveSubjectCheck = false,
  skipMergeStandardsAssociatedParents = false,
  shouldGenerateFlatHierarchyWithValues = false,
  includeNodeSet,
}) => {
  const nodes = _.get(resolvedValue, "nodes", []);
  const config = _.get(fieldObj, "config", {});
  const dynamicSubject = _.get(fieldObj, "dynamicSubject", {});

  const plannerElementConfig = _.get(config, "plannerElementConfig", {}) || {};
  const { type } = plannerElementConfig;

  // Following condition is generic to handle nested element or PlannerElementDataSet
  if (
    _.get(fieldObj, "viewType") === "Nestedview" ||
    _.get(fieldObj, "viewType") === "PlannerElementDataSet"
  ) {
    let associatedParentNodes = getUniqAssociatedParentsOfNodes({ nodes });
    let updatedNodes = nodes;
    if (_.isEmpty(subjectIds) && deriveSubjectIdsFromNodes) {
      subjectIds = _.map(
        _.filter(
          associatedParentNodes,
          item => _.get(item, "__typename") == "Subject"
        ),
        item => item.id
      );
    }

    //following condition is given to remove the subjects which are selected but removed afterwards
    if (
      _.includes(["MYP_LEARNING_STANDARD", "UBD_LEARNING_STANDARD"], type) &&
      !skipRemoveSubjectCheck
    ) {
      const subjects = _.map(subjectIds, subjectId => {
        return { id: subjectId };
      });

      const updatedValues = _.filter(updatedNodes, updatedNode => {
        return (
          _.intersectionBy(
            subjects,
            getNodeParents(updatedNode, "SUBJECT"),
            "id"
          ).length > 0
        );
      });

      updatedNodes = updatedValues;
    }
    if (
      _.includes(
        [
          "MYP_RELATED_CONCEPT",
          "MYP_SPECIFIED_CONCEPTS",
          "MYP_LEARNING_STANDARD",
          "MYP_ATL",
          "UBD_LEARNING_STANDARD",
          "UBD_ATL",
          "DP_ATL",
        ],
        type
      )
    ) {
      let filteredSubjectIds = subjectIds;

      if (!_.isEmpty(dynamicSubject)) {
        filteredSubjectIds = [_.get(fieldObj, "dynamicSubject.id", "")];
      }

      if (!_.isEmpty(filteredSubjectIds)) {
        associatedParentNodes = _.filter(associatedParentNodes, node =>
          _.includes(filteredSubjectIds, node.id)
        );
      }

      // In this case filter nodes that are type Subject only to avoid showing wrong grades in MYP_ATL and UBD_ATL
      associatedParentNodes = _.filter(
        associatedParentNodes,
        node => _.get(node, "__typename") == "Subject"
      );
    }

    if (
      _.size(associatedParentNodes) > 1 &&
      !_.isEqual(type, "UBD_LEARNING_STANDARD") &&
      !skipMergeStandardsAssociatedParents
    ) {
      updatedNodes = mergeAssociatedParentsWithNodes({
        associatedParentNodes,
        nodes: updatedNodes,
      });
    }

    if (shouldGenerateFlatHierarchyWithValues) {
      return generateFlatHierarchyWithValues({
        nodes: updatedNodes,
        values: value,
        includeNodeSet,
      }).flatNodes;
    }
    return generateNestedSelectedData({
      ...getPYPElementSetFromNodes({ nodes: updatedNodes }),
      value,
    });
  } else {
    if (
      _.includes(
        [
          "DP_CAS_STRANDS",
          "DP_CAS_SERVICE_TYPES",
          "DP_CP_SL_APPROACHES",
          "DP_CAS_APPROACHES",
          "DP_CP_SL_STRANDS",
          "MYP_SERVICE_ACTION_TYPE",
        ],
        type
      )
    ) {
      const hasDisplaySeq = !_.some(
        nodes,
        node => !_.get(node, "displaySequence", null)
      );

      if (hasDisplaySeq) {
        const updatedValue = value
          ? _.orderBy(
              value,
              item => _.find(nodes, { id: item })?.displaySequence
            )
          : value;
        return getPlannerElementLabel({ nodes, value: updatedValue });
      } else {
        const updatedValue = value ? value.sort() : value;
        return getPlannerElementLabel({ nodes, value: updatedValue });
      }
    }
    return getPlannerElementLabel({ nodes, value });
  }
};

const getViewModeValueForMultiWindowTable = ({ fieldObj, valueKeys }) => {
  const viewSubFields = _.get(fieldObj, "viewSubFields", []);

  const valuesBySubFields = _.reduce(
    _.map(_.get(fieldObj, "subFields", []), "key", null),
    (result, subField) => {
      return { ...result, [subField]: [] };
    },
    {}
  );

  _.forEach(valueKeys, ({ tableRowsValues }) => {
    _.forEach(tableRowsValues, ({ subFieldValues }) => {
      _.forEach(subFieldValues, ({ id, values: subFieldValues }) => {
        const isPresentInView = _.includes(viewSubFields, id);

        if (isPresentInView) {
          const subFieldObj = _.find(
            _.get(fieldObj, "subFields", []),
            ({ key }) => key === id
          );

          valuesBySubFields[id] = [
            ...valuesBySubFields[id],
            ...getElementValue({
              valueKeys: subFieldValues,
              fieldObj: subFieldObj,
            }),
          ];
        }
      });
    });
  });

  return valuesBySubFields;
};

const getValueForListWithTags = ({ fieldObj, valueKeys }) => {
  const options = _.get(fieldObj, "config.tagOptions", []);

  const updatedValues = _.reduce(
    valueKeys,
    (result, { value, tag, id }) => {
      const tagLabel = _.get(
        _.find(options, ({ value: currentTag }) => currentTag == tag),
        "label",
        null
      );

      return [...result, { value, tag: tagLabel, id }];
    },
    []
  );

  return updatedValues;
};

export const checkEmptyElementValue = ({
  fieldKey,
  data,
  unitPlanFields,
  isUnitPlan = false,
  fieldObj,
  ignoreUnitPlanSubjectCheck,
  isCommunity = false,
  curriculumProgramType,
  isToddleLibrary = false,
  checkOnlyValueKeysForBenchmarks = false,
  getElementValue: _getElementValue,
}) => {
  const allFields = _.unionBy(
    _.get(data, "allFields", []),
    _.get(data, "fields", []),
    "id"
  );
  const dataTofind = _.find(allFields, value => {
    return _.isEqual(_.get(value, "uid"), fieldKey);
  });

  const responses = _.get(dataTofind, "responses.edges", []);

  const unitPlanFieldData = _.find(unitPlanFields, { uid: fieldKey });

  const unitPlanFieldValue = _.get(unitPlanFieldData, "value", "");

  const valueKeys = _.get(dataTofind, "value", "");

  const subjects = _.find(unitPlanFields, { uid: "subjects" });
  const subjectIds = _.get(subjects, "value", []);

  const assessmentType = getFieldValue({
    fields: allFields,
    uid: "assessmentType",
  });
  const isWorksheet = _.isEqual(assessmentType, ASSESSMENT_TYPE_WORKSHEET);
  const resolvedValue = _.get(dataTofind, "resolvedMinimalTree", "");
  switch (fieldKey) {
    case "image": {
      return (
        !_.isEmpty(_.get(resolvedValue, "attachment", null)) ||
        !_.isEmpty(_.get(dataTofind, "value", null))
      );
    }
    case "enableRating": {
      return _.get(data, "isLearningGoalsEvaluationEnabled", false);
    }
    case "aiEvaluation": {
      return valueKeys;
    }
    case "addLearningGoals": {
      // Inlined from TaggableElementsUtils.getAllTaggableElements to avoid a
      // circular import (it filters via checkEmptyElementValue). Mirrors the
      // shouldLocalizeLabel=false / no-plannerElementSets call shape.
      let taggableElementsForCheck;
      switch (curriculumProgramType) {
        case CURRICULUM_TYPE_DP:
          taggableElementsForCheck = TAGGABLE_DP_ELEMENTS_V2;
          break;
        case CURRICULUM_TYPE_MYP:
          taggableElementsForCheck = TAGGABLE_MYP_ELEMENTS_V2;
          break;
        case CURRICULUM_TYPE_UBD:
          taggableElementsForCheck = TAGGABLE_UBD_ELEMENTS;
          break;
        default:
          taggableElementsForCheck = TAGGABLE_PYP_ELEMENTS_V2;
      }
      const nonEmptyTaggableElements = _.filter(
        taggableElementsForCheck,
        ({ value }) =>
          checkEmptyElementValue({
            fieldKey: value,
            data: { allFields },
            ignoreUnitPlanSubjectCheck: true,
            unitPlanFields,
            checkOnlyValueKeysForBenchmarks: true,
          })
      );
      return !_.isEmpty(nonEmptyTaggableElements);
    }
    case "disciplinaryContent": {
      const contentFields = _.filter(unitPlanFields, field =>
        _.includes(field.uid, "disciplinaryContent")
      );
      const filteredFields = _.filter(contentFields, field =>
        _.get(_.find(unitPlanFields, { uid: field.uid }), "value", null)
      );

      return !_.isEmpty(filteredFields);
    }
    case "les":
    case "fmtAssessments":
    case "digAssessments":
    case "smtAssessments":
      return (
        _.get(data, "assessmentLibrary.assessmentCount", 0) > 0 ||
        _.get(data, "leLibrary.leCount", 0) > 0
      );
    case "relatedConcepts": {
      const subjectsToFind = _.get(data, "subjects.value", []);
      return (
        _.get(valueKeys, "length", 0) != 0 &&
        _.get(subjectsToFind, "length", 0) != 0
      );
    }
    case "resources":
      return _.get(data[fieldKey], "totalCount", 0) > 0;
    case "plannerResources": {
      const organizationResources = _.get(data, "organizationResources", {});

      if (isWorksheet && _.isArray(organizationResources)) {
        const organizationResource = _.filter(
          organizationResources,
          ({ type }) => type != ORG_RESOURCE_TYPE_WORKSHEET
        );
        return !_.isEmpty(organizationResource);
      }

      if (_.has(organizationResources, "edges"))
        return _.size(organizationResources?.edges) > 0;
      else return _.size(organizationResources) > 0;
    }
    case "rubric":
    case "checklist":
    case "singlePointRubric":
    case "exemplar":
    case "anecdotal":
    case "measureAssessing": {
      const assessmentToolEdges = _.get(data, "assessmentTools.edges", []);
      // in DP curriculum score and comment are shown in different section and not part of assessment tools
      // so filter these tools from assessment tools array
      if (curriculumProgramType === CURRICULUM_TYPE_DP || isWorksheet) {
        const assessmentTools = _.filter(assessmentToolEdges, ({ node }) => {
          const assessmentToolType = _.get(node, "assessmentToolType");
          return !_.includes(["COMMENT", "SCORE"], assessmentToolType);
        });

        return !_.isEmpty(assessmentTools);
      }

      return !_.isEmpty(assessmentToolEdges);
    }
    case "score": {
      const { isScoreEnabled } = getScoreDetailsMemoize({
        assessmentTools: _.get(data, "assessmentTools", []),
      });

      return isScoreEnabled;
    }
    case "analysisScale": {
      const assessmentTools = _.get(data, "assessmentTools.edges", []);
      const analysisScale = _.find(assessmentTools, ({ node }) => {
        return node?.assessmentToolType === "ANALYSIS";
      });

      return !_.isEmpty(analysisScale);
    }
    case "weightage": {
      return !_.isEmpty(_.toString(_.get(data, "weightage", null)));
    }

    case "conceptsQuestion":
      return true;
    case "actionQuestion":
      return true;
    case "studentAction":
      return true;
    case "finalRemarks": {
      const assessmentTools = _.get(data, "assessmentTools.edges", []);
      const commentTool = _.find(assessmentTools, ({ node }) => {
        return node?.assessmentToolType === "COMMENT";
      });

      return !_.isEmpty(commentTool);
    }
    // case "exemplar":
    //   return (
    //     !_.isEmpty(_.get(data, "assessmentTool.message", "")) ||
    //     !_.isEmpty(_.get(data, "assessmentTool.attachments", []))
    //   );
    // case "anecdotal":
    //   return !_.isEmpty(_.get(data, "assessmentTool.message", ""));
    case "benchmarks": {
      if (checkOnlyValueKeysForBenchmarks) {
        return !_.isEmpty(valueKeys);
      }

      const benchmarksD0 = _.filter(
        _.get(resolvedValue, "benchmarks", []),
        benchmark => {
          return benchmark.depth == 0;
        }
      );

      if (ignoreUnitPlanSubjectCheck) {
        //TODO: hack for classroom LE - remove later
        return !_.isEmpty(benchmarksD0);
      }

      const subjects = _.find(unitPlanFields, { uid: "subjects" });

      const subjectValues = _.map(
        _.get(subjects, "resolvedMinimalTree.subjects", []),
        subject => {
          return { ...subject.benchmarkRootNode };
        }
      );

      // HACK: In the community no unitplan subject reference is there
      if (isCommunity || isToddleLibrary) {
        return !_.isEmpty(benchmarksD0);
      } else {
        return !_.isEmpty(_.intersectionBy(benchmarksD0, subjectValues, "id"));
      }
    }
    case "unitPlan": {
      const resolvedUnitPlan = _.get(resolvedValue, "unitPlan", {});
      return !_.isEmpty(resolvedUnitPlan);
    }
    case "assessmentCategory": {
      return !_.isEmpty(data?.category) || !_.isEmpty(data?.academicTerm);
    }
    case "pedagogicalApproachDP": {
      const hasValue = _.every(valueKeys, key => _.has(key, "value"))
        ? !_.isEmpty(
            _.filter(valueKeys, key => _.get(key, "isSelected", false))
          )
        : !_.isEmpty(valueKeys);

      return hasValue;
    }
    case "assessmentType": {
      return !_.isEmpty(valueKeys);
    }

    case "goals": {
      const goals = _.get(dataTofind, "value", []);
      return _.some(goals, goal => !_.isEmpty(goal?.value?.trim()));
    }
    case "studentResponse":
      return true;
    default: {
      //Handled for Assessment Tools
      if (_.get(fieldObj, "type", "") == "AssessmentTool") {
        return !_.isEmpty(_.get(data, "assessmentTools.edges", null));
      }

      if (_.get(fieldObj, "config.isMappedWithDetailsFields", false)) {
        const mappedDetailsFields = _.get(
          fieldObj,
          "config.mappedDetailsFields"
        );
        _.forEach(mappedDetailsFields, val => {
          const result = _.get(
            _.find(unitPlanFields, { uid: val }),
            "value",
            null
          );
          if (_.isEmpty(result)) return false;
        });
        return true;
      }
      // This is done specifically for handling unitplan fields which are having different query fragments
      const getElementValueFunc = _getElementValue || getElementValue;
      const value = getElementValueFunc({
        fieldUID: fieldKey,
        unitPlanFieldValue,
        valueKeys,
        isUnitPlan,
        resolvedValue,
        fieldObj,
        subjectIds,
        unitPlanFields,
        skipRemoveSubjectCheck: ignoreUnitPlanSubjectCheck,
      });
      //TODO:refactor below statements in switch case
      if (_.get(fieldObj, "type", "") == "MultiField") {
        return !_.isEmpty(_.filter(value, val => !_.isEmpty(val)));
      }

      if (_.get(fieldObj, "type", "") == "IBReflection") {
        return !_.isEmpty(responses);
      }

      if (_.get(fieldObj, "type", "") === "MultiWindowTable") {
        return getMultiWindowTableCompletionStatus({
          fieldObj,
          valueKeys,
          subjectIds,
          allFields,
        });
      }

      if (_.get(fieldObj, "type", "") === "CheckListWithPopup") {
        return !getCheckListWithPopupEmptyStatusMemoized({
          unitPlanFields,
          valueKeys,
          fieldKey,
          dataField: _.get(fieldObj, "dataField", null),
        });
      }

      if (_.get(fieldObj, "type", "") === "ActionTable") {
        return !_.isEmpty(_.get(value, "rows", []));
      }

      if (_.get(fieldObj, "type", "") === "GenericCardList") {
        const isArrayOfStrings = _.every(value, item => {
          return _.isString(item);
        });

        // if structure is like ["Who we are", "..."]
        if (isArrayOfStrings) {
          return !_.isEmpty(value);
        }

        // if structure is like [{ isSelected: true, value: "Who we are", id: 1 }, {...}]
        return !_.isEmpty(value) && _.some(value, item => item?.isSelected);
      }

      if (_.get(fieldObj, "type", "") === "InputNumberList") {
        const hasSelection = _.get(
          fieldObj,
          "config.fieldTypeConfig.hasSelection",
          false
        );

        if (hasSelection) {
          return !_.isEmpty(value) && _.some(value, item => item?.isSelected);
        }

        return (
          !_.isEmpty(value) &&
          _.some(value, item => {
            if (_.has(item, "value")) {
              return !_.isEmpty(item.value);
            }
            return !_.isEmpty(item);
          })
        );
      }

      if (_.isNumber(value)) {
        return true;
      } else if (_.isString(value)) {
        // special handling for video tag as it not support by default by html-to-text lib
        let options = {};
        if (_.includes(value, "<video")) {
          options = {
            tags: {
              video: { format: "videoFormatter" },
            },
            formatters: {
              // special case for video tag
              videoFormatter: (elem, walk, builder, formatOptions) => {
                const title = _.get(elem, "attribs.title");
                const text = title || "[Video]";
                builder.addInline(text);
              },
            },
          };
        }
        return (
          !_.isEmpty(_.trim(htmlToText(value, options))) ||
          checkForIframeEmbedInHtmlString(value)
        );
      } else {
        return !_.isEmpty(value);
      }
    }
  }
};

/**
 * This function will return true if value for checkListWithPopup is empty,
 * or it has values that are not present in unit plan fields.
 */
const getCheckListWithPopupEmptyStatus = ({
  unitPlanFields,
  valueKeys,
  fieldKey,
  dataField,
}) => {
  if (_.isEmpty(valueKeys)) {
    return true;
  }

  const unitPlanValueKeys = _.get(
    _.find(unitPlanFields, ({ uid }) => uid === fieldKey),
    "value",
    null
  );

  const fieldValues = getFieldValuesOfMultiWindowTableMemoized({
    valueKeys: unitPlanValueKeys,
    fieldKey: dataField,
  });

  const fieldValueIds = _.map(fieldValues, ({ id }) => id);
  const assessmentFieldValueIds = _.map(valueKeys, ({ id }) => id);
  if (_.isEmpty(_.intersection(fieldValueIds, assessmentFieldValueIds))) {
    return true;
  }

  return false;
};

const getCheckListWithPopupEmptyStatusMemoized = _.memoize(
  getCheckListWithPopupEmptyStatus,
  params => JSON.stringify(params)
);

const getMultiWindowTableCompletionStatus = ({
  fieldObj,
  valueKeys,
  subjectIds,
  allFields,
}) => {
  return _.some(valueKeys, ({ tableRowsValues }) => {
    return _.some(tableRowsValues, ({ subFieldValues }) => {
      return _.some(subFieldValues, ({ values, id }) => {
        const subFieldConfig = _.find(
          _.get(fieldObj, "subFields", []),
          ({ key }) => key === id
        );
        const isFilteredByFieldPorted = _.get(
          subFieldConfig,
          "config.isFilteredByFieldPorted"
        );

        const filteredBy = _.get(subFieldConfig, "config.filteredBy", []);

        let resolvedMinimalTree = {};

        _.forEach(filteredBy, fieldUid => {
          resolvedMinimalTree = _.get(
            _.find(allFields, ({ uid }) => uid == fieldUid),
            "resolvedMinimalTree",
            {}
          );
        });

        const availableValues = isFilteredByFieldPorted
          ? getElementValueForPortedFilteredByField({
              resolvedMinimalTree,
              value: values,
            })
          : getElementValue({
              fieldObj: subFieldConfig,
              valueKeys: values,
              subjectIds,
              resolvedValue: resolvedMinimalTree,
            });

        return !_.isEmpty(availableValues);
      });
    });
  });
};

const getFieldValuesOfMultiWindowTable = ({ valueKeys, fieldKey }) => {
  const fieldValues = [];
  _.forEach(valueKeys, ({ tableRowsValues }) => {
    _.forEach(tableRowsValues, ({ subFieldValues }) => {
      _.forEach(subFieldValues, ({ id, values }) => {
        if (id === fieldKey) {
          fieldValues.push(...values);
        }
      });
    });
  });

  return fieldValues;
};

const getFieldValuesOfMultiWindowTableMemoized = _.memoize(
  getFieldValuesOfMultiWindowTable,
  params => JSON.stringify(params)
);

const getNotPypInputListLabel = ({
  values,
  unitPlanFieldValue,
  isUnitPlan,
}) => {
  if (isUnitPlan) {
    return _.filter(
      _.map(values, val => val.value),
      val => !!_.trim(val)
    );
  } else {
    return _.filter(
      _.map(values, value => {
        const valueObject = _.find(unitPlanFieldValue, val => val.id == value);
        return _.get(valueObject, "value", "");
      }),
      val => !!_.trim(val)
    );
  }
};

export const getResolvedOptimisticValue = ({
  fieldUID,
  params,
  value,
  resolvedValue,
  fieldTemplateObj,
}) => {
  const setKey = pypElementSetMapping[fieldUID];

  switch (fieldUID) {
    case "score":
      return {
        ...resolvedValue,
        isUsingScore: _.get(value, "isUsingScore", null),
        maxScore: _.get(value, "maxScore", null),
        category: !_.isEmpty(params) ? { ...params } : null,
      };
    case "unitPlan": {
      return {
        ...resolvedValue,
        unitPlan: params,
      };
    }
    case "benchmarks":
    case "atls": {
      if (isSinglePageAndNewUnitView()) {
        const nodes =
          buildSlimTree({
            nodes: mergeArraysById(
              _.get(resolvedValue, setKey, []),
              _.get(params, "nodes", [])
            ),
            selIds: value,
            includeDescendants: false,
          }) || [];
        return {
          ...resolvedValue,
          [setKey]: _.sortBy(nodes, ["depth", "displaySequence"]),
        };
      }

      return {
        ...resolvedValue,
        [setKey]: _.uniqBy(
          [
            ..._.filter(
              _.get(resolvedValue, setKey, []),
              item => _.get(item, params.rootNodeKey, "") != params.rootNodeId
            ),
            ...(buildSlimTree({
              nodes: _.get(params, "nodes", []),
              selIds: _.filter(value, id =>
                _.includes(
                  _.map(_.get(params, "nodes", []), item => item.id),
                  id
                )
              ),
            }) || []),
          ],
          "id"
        ),
      };
    }
    case "subjects":
    case "concepts":
    case "action":
    case "lp":
    case "theme":
    case "relatedConcepts":
    case "subjectGroup": {
      if (_.isArray(params?.nodes)) {
        return {
          ...resolvedValue,
          [setKey]: _.map(
            _.filter(_.get(params, "nodes", []), item =>
              _.includes(value, item?.node?.id || item?.id)
            ),
            nodeItem => nodeItem?.node || nodeItem
          ),
        };
      }
      return {
        ...resolvedValue,
        [setKey]: _.isEqual(value, [_.get(params, "nodes.node.id", "")])
          ? [_.get(params, "nodes.node", {})]
          : [],
      };
    }
    case "voiceInstruction":
    case "videoAttachment":
      return {
        ...resolvedValue,
        attachment: value,
      };
    // case "studentTemplate": {
    //   return resolvedValue;
    // }

    case "tags": {
      let tags = [];
      _.forEach(value, id => {
        const item = _.find(params, item => item.id == id);
        if (!_.isEmpty(item)) {
          tags = [...tags, item];
        }
      });
      return { ...resolvedValue, tags };
    }
    case "subjectLevel": {
      let genericTags = [];
      _.forEach(value, id => {
        const item = _.find(
          _.get(params, "nodes", []),
          item => item.value == id
        );
        if (!_.isEmpty(item)) {
          genericTags = [
            ...genericTags,
            { id: item.value, label: item.label, __typename: "GenericTag" },
          ];
        }
      });
      return { ...resolvedValue, genericTags };
    }
    case "objectiveLevel": {
      const objectiveLevelNodes = _.get(params, "objectiveLevelNodes", []);

      return {
        ...resolvedValue,
        nodes: !_.isEmpty(objectiveLevelNodes)
          ? objectiveLevelNodes
          : _.filter(_.get(resolvedValue, "nodes", []), node =>
              _.includes(value, node.id)
            ),
      };
    }
    case "stimulus": {
      const attachmentValue = _.get(params, "value", {});
      return {
        ...resolvedValue,
        attachments: !_.isEmpty(value) ? attachmentValue : [],
      };
    }
    default: {
      const type = _.get(fieldTemplateObj, "config.type", "");
      // The post condition is used for ported fields
      const isPlannerElement =
        type === "PLANNER_ELEMENT" ||
        _.has(fieldTemplateObj, "plannerElementSetId");

      const updatedParams = Array.isArray(_.get(params, "nodes", []))
        ? _.get(params, "nodes", [])
        : [_.get(params, "nodes", {})];

      //Handled Planner Elements
      if (isPlannerElement) {
        //Creating nodes using value and combining old nodes and new nodes
        const updatedNodes =
          buildSlimTree({
            nodes: mergeArraysById(
              _.get(resolvedValue, "nodes", []),
              updatedParams
            ),
            selIds: !_.isArray(value) && value ? [value] : value,
            includeDescendants: false,
          }) || [];

        //Sorting nodes by depth & displaySequence
        const sortedNodes = _.sortBy(updatedNodes, [
          "depth",
          "displaySequence",
        ]);

        return {
          ...resolvedValue,
          nodes: sortedNodes,
        };
      }
      return resolvedValue;
    }
  }
};

const getBenchMarkValue = ({
  values,
  benchmarkSet,
  nodes,
  shouldGenerateParentChildRelation,
  startDepth,
  shouldGenerateFlatHierarchyWithValues = false,
  includeNodeSet = true,
}) => {
  if (shouldGenerateFlatHierarchyWithValues) {
    return generateFlatHierarchyWithValues({
      nodes,
      values,
      includeNodeSet,
    }).flatNodes;
  }
  return benchmarkSet
    ? generateNestedSelectedData({
        nodes: _.get(benchmarkSet, "nodes", []),
        rootNodes: _.get(benchmarkSet, "rootNodes", []),
        value: values,
        shouldGenerateParentChildRelation,
      })
    : generateNestedSelectedData({
        ...getPYPElementSetFromNodes({ nodes, startDepth }),
        value: values,
        shouldGenerateParentChildRelation,
      });
};

export const getBenchMarkValueMemoized = _.memoize(getBenchMarkValue, params =>
  JSON.stringify(params)
);

const getValueFromLabelValuePair = ({
  values,
  fieldObj,
  fieldUID,
  t,
  isLocalisedTemplate = true,
}) => {
  const options = _.get(fieldObj, "config.options", []);

  if (t && isLocalisedTemplate) {
    switch (fieldUID) {
      case "measureAssessing":
        if (values === "") {
          return values ? t("common:none") : null;
        }
        return values
          ? t(_.get(_.find(ASSESSMENT_TOOLS, { type: values }), "locale", ""))
          : null;
      case "evaluationType":
        if (_.isArray(values)) {
          return _.map(values, val => {
            return val
              ? t(_.get(_.find(EVALUATION_METHOD, { type: val }), "locale", ""))
              : null;
          });
        }
        return values
          ? t(_.get(_.find(EVALUATION_METHOD, { type: values }), "locale", ""))
          : null;
      case "size":
        return values
          ? t(_.get(_.find(SIZE, { type: values }), "locale", ""))
          : null;
      default:
        if (_.isArray(values)) {
          return _.map(values, val => {
            return val
              ? _.get(_.find(options, { value: val }), "label", "")
              : null;
          });
        } else {
          return values
            ? _.get(_.find(options, { value: values }), "label", "")
            : null;
        }
    }
  } else {
    if (_.isArray(values)) {
      return _.map(values, val => {
        return val ? _.get(_.find(options, { value: val }), "label", "") : null;
      });
    } else {
      return values
        ? _.get(_.find(options, { value: values }), "label", "")
        : null;
    }
  }
};

const getLists = ({ values }) => {
  return _.map(values, val => (typeof val == "object" ? val.value : val));
};

export const getElementData = ({
  elements,
  unitPlanFields,
  benchmarkSet,
  field_list,
  isPost = false,
  deriveSubjectIdsFromNodes = false,
  skipRemoveSubjectCheck = false,
  t,
  shouldGenerateParentChildRelation = false,
}) => {
  const elementData = {};

  _.forEach(elements, item => {
    const { fieldUID, value: valueKeys, resolvedMinimalTree } = item;
    const fieldKeys = _.split(fieldUID, "_");
    const { showFlatList, isStandards } = shouldShowFlatListForType({
      fieldUID,
    });

    const shouldGenerateFlatHierarchyWithValues = showFlatList;

    elementData[fieldUID] = getElementValue({
      fieldUID,
      valueKeys,
      isPost,
      deriveSubjectIdsFromNodes,
      unitPlanFieldValue: _.get(
        _.find(unitPlanFields, { uid: fieldUID }),
        "value",
        ""
      ),
      fieldObj: _.get(field_list, `${_.get(fieldKeys, "0")}`, {}),
      benchmarkSet,
      resolvedValue: isPost
        ? resolvedMinimalTree
        : !_.isEmpty(_.find(unitPlanFields, { uid: fieldUID }))
        ? _.get(
            _.find(unitPlanFields, { uid: fieldUID }),
            "resolvedMinimalTree",
            ""
          )
        : resolvedMinimalTree,
      skipRemoveSubjectCheck,
      t,
      shouldGenerateParentChildRelation,
      shouldGenerateFlatHierarchyWithValues,
      includeNodeSet: isStandards,
    });
  });

  return elementData;
};
